import React, { useState, useRef, useEffect } from "react";

import { useTranslation } from "react-i18next";
import Select from "@atlaskit/select";

import Footer from "./Footer";
import ArrowIcon from "./icons/ArrowIcon";
import IntelligenceIcon from "./icons/IntelligenceIcon";
import {
  Heading,
  Container,
  SendButton,
  Border,
  Parameters,
  Textarea,
} from "./styled";
import { restService, checkIfRequestActive, checkIfAdmin } from "./RestService";
import { LANGUAGE_OPTIONS, getToneOptions, getIconOptions } from "./parameters";
import { configurationService } from "../../../../../configuration/ConfigurationService";
import { useCardsStore } from "../../../../../stores/use-cards-store";
import { useClickOutside } from "../../../../Shared/Hooks/useClickOutside";
import { analytics } from "../../../../../analytics";

function Prompt({ isSaving, onSave }) {
  const { t } = useTranslation();

  const { data, setData, isPromptLoading, setIsPromptLoading } = useCardsStore(
    (state) => ({
      data: state.data,
      setData: state.setData,
      isPromptLoading: state.isPromptLoading,
      setIsPromptLoading: state.setIsPromptLoading,
    })
  );

  const { config } = data[0];

  const [prompt, setPrompt] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [isAdmin, setIsAdmin] = useState(null);
  const [hasAI, setHasAI] = useState(false);
  const [hasAIchanges, setHasAIchanges] = useState(false);
  const [errorCode, setErrorCode] = useState(null);

  const iconOptions = getIconOptions(t);

  const [emojiEnabled, setEmojiEnabled] = useState(iconOptions[0]);
  const [parameters, setParameters] = useState({
    tone: null,
    language: null,
  });

  const promptRef = useRef(null);
  const toneOptions = getToneOptions(t);

  useClickOutside(promptRef, () => setIsActive(false));

  useEffect(() => {
    setHasAIchanges(false);

    if (config.design === "emoji-one") {
      if (!emojiEnabled.value) {
        setEmojiEnabled(iconOptions[1]);
      }
    }
  }, [data]);

  useEffect(() => {
    if (isSaving && !prompt) {
      onSave();
    }
  }, [isSaving]);

  useEffect(() => {
    checkIfAdmin().then(setIsAdmin);

    configurationService.getCardsConfiguration().then((res) => {
      const { intelligenceEnablement } = JSON.parse(res.data);

      setHasAI(intelligenceEnablement);
    });
  }, []);

  const onGenerateHandler = (input) => {
    if (!input || isPromptLoading) return;

    analytics.logEvent({ name: "Cards AI", template: "insert" });
    setIsPromptLoading(true);

    restService
      .generateCards(input, parameters, emojiEnabled.value, {
        config,
        cards: data,
      })
      .then((data) => {
        if (checkIfRequestActive()) {
          setData(
            data.cards.map((card) => ({ config: data.config, card })),
            true
          );
          setPrompt("");
          setHasAIchanges(true);
          setErrorCode(null);
          setIsPromptLoading(false);
        }
      })
      .catch((err) => {
        if (checkIfRequestActive()) {
          setErrorCode(err?.response?.status || 500);
          setIsPromptLoading(false);
          analytics.logEvent({ name: "Cards AI error" });
        }
      });
  };

  if (isPromptLoading || !hasAI || isAdmin === null) return null;

  return (
    <Container>
      <div ref={promptRef} style={{ width: "100%" }}>
        {isActive ? (
          <Parameters>
            <Select
              placeholder={t("select-language")}
              menuPlacement="top"
              options={LANGUAGE_OPTIONS}
              value={parameters.language}
              onChange={(language) =>
                setParameters({ ...parameters, language })
              }
            />

            <Select
              placeholder={t("select-tone")}
              menuPlacement="top"
              options={toneOptions}
              value={parameters.tone}
              onChange={(tone) => setParameters({ ...parameters, tone })}
            />

            {["emoji", "icon"].includes(config.type) && (
              <Select
                placeholder={t("icon")}
                menuPlacement="top"
                isDisabled={config.design === "emoji-one"}
                onChange={(value) => setEmojiEnabled(value)}
                options={iconOptions}
                value={emojiEnabled}
              />
            )}
          </Parameters>
        ) : (
          <Heading>
            <IntelligenceIcon /> {t("cards-ai.title")}
          </Heading>
        )}

        <Border isActive={isActive} onClick={() => setIsActive(true)}>
          <SendButton
            onClick={() => onGenerateHandler(prompt)}
            isDisabled={!prompt || isPromptLoading}
          >
            <ArrowIcon />
          </SendButton>

          <Textarea
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              const keycode = e.charCode || e.keyCode;
              if (keycode === 13) {
                e.preventDefault();
                onGenerateHandler(e.target.value);
              }

              const input = promptRef.current.querySelector("textarea");

              input.style.height = "inherit";
              input.style.height = `${input.scrollHeight}px`;
            }}
            maxlength="250"
            placeholder={t("cards-ai.placeholder")}
            rows={1}
          >
            {prompt}
          </Textarea>
        </Border>

        <Footer
          prompt={prompt}
          errorCode={errorCode}
          hasAIchanges={hasAIchanges}
          onGenerateHandler={onGenerateHandler}
          setErrorCode={setErrorCode}
        />
      </div>
    </Container>
  );
}

export default Prompt;
